import React from 'react';
import { styled } from '@compiled/react';
import { parseISO } from 'date-fns';
import Button, { ButtonGroup } from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { BAD_DEPLOYMENTS_DATA_END_DATE } from '../../../common/constants';
import {
	useFireDateFilterSelectedAnalytics,
	useFireFormCancelledAnalytics,
} from '../../../common/utils/analytics';
import { useDateFilter } from '../../../controllers/date-filter';
import Calendar from './calendar';
import DateInput from './date-input';
import messages from './messages';
import { useDateRangePickerForm } from './utils';

type Props = {
	onClose: () => void;
};

const disabledDatesBeforeBadDeploymentsEndDate = [
	new Date(parseISO('2020-03-29')),
	new Date(parseISO('2020-03-30')),
	new Date(parseISO('2020-03-31')),
];

const useFireFormSubmittedAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (duration: number) =>
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'submitted',
				actionSubject: 'form',
			}),

			'dateRangePickerFormSubmit',
			{
				duration,
				durationInDays: Math.round(duration / (1000 * 60 * 60 * 24)),
			},
		);
};

const DateRangePicker = ({ onClose }: Props) => {
	const { formatMessage } = useIntl();
	const [, { setCustomDateFilter }] = useDateFilter();
	const {
		startDate,
		endDate,
		startValidation,
		endValidation,
		startDateInputChangeHandler,
		endDateInputChangeHandler,
		startCalendarSelectHandler,
		endCalendarSelectHandler,
		isFormValid,
	} = useDateRangePickerForm();
	const fireFormSubmittedAnalytics = useFireFormSubmittedAnalytics();
	const fireFormCancelledAnalytics = useFireFormCancelledAnalytics();
	const fireDateFilterSelectedAnalytics = useFireDateFilterSelectedAnalytics();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const submitHandler = (event: any) => {
		event.preventDefault();
		if (startDate && endDate) {
			setCustomDateFilter({ startDate, endDate });
			// @ts-expect-error - TS2362 - The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type. | TS2363 - The right-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
			fireFormSubmittedAnalytics(endDate - startDate);
			fireDateFilterSelectedAnalytics('custom-date');
			onClose();
		}
	};

	return (
		<ContextualAnalyticsData sourceName="viewDateRangePicker" sourceType={MODAL}>
			<FireScreenAnalytics />
			<StyledForm onSubmit={submitHandler} name="date-filter-form">
				<FieldWrapper>
					<DateInput
						testId="software-deployments.ui.date-filter.date-range-picker.date-input-start-date"
						label={formatMessage(messages.startDateLabel)}
						isInvalid={startValidation && startValidation.type === 'ERROR'}
						onChange={startDateInputChangeHandler}
						validation={startValidation}
						selected={startDate}
					/>

					<Calendar
						testId="software-deployments.ui.date-filter.date-range-picker.calendar-start-date"
						disabledDates={disabledDatesBeforeBadDeploymentsEndDate}
						disableFuture
						minDate={BAD_DEPLOYMENTS_DATA_END_DATE}
						selected={startDate}
						onSelect={startCalendarSelectHandler}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<DateInput
						testId="software-deployments.ui.date-filter.date-range-picker.date-input-end-date"
						label={formatMessage(messages.endDateLabel)}
						isInvalid={endValidation && endValidation.type === 'ERROR'}
						onChange={endDateInputChangeHandler}
						validation={endValidation}
						selected={endDate}
					/>

					<Calendar
						testId="software-deployments.ui.date-filter.date-range-picker.calendar-end-date"
						disabledDates={disabledDatesBeforeBadDeploymentsEndDate}
						disableFuture
						minDate={startDate ?? BAD_DEPLOYMENTS_DATA_END_DATE}
						selected={endDate}
						onSelect={endCalendarSelectHandler}
					/>

					<FormActions>
						<ButtonGroup>
							<Button
								appearance="subtle"
								onClick={() => {
									onClose();
									fireFormCancelledAnalytics();
								}}
							>
								{formatMessage(messages.cancelFormSubmission)}
							</Button>
							<Button
								testId="software-deployments.ui.date-filter.date-range-picker.submit"
								appearance="primary"
								type="submit"
								isDisabled={!isFormValid}
							>
								{formatMessage(messages.confirmFormSubmission)}
							</Button>
						</ButtonGroup>
					</FormActions>
				</FieldWrapper>
			</StyledForm>
		</ContextualAnalyticsData>
	);
};

export default DateRangePicker;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledForm = styled.form({
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldWrapper = styled.div({
	width: '321px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormActions = styled.div({
	marginTop: token('space.200', '16px'),
	display: 'flex',
	justifyContent: 'flex-end',
	paddingBottom: token('space.300', '24px'),
});
